/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import ContentShell from "components/ContentShell";
import OperatorMultiSelect from "components/OperatorMultiSelect";
import { useSnackbar } from "hooks/useSnackbar";
import { getFatigueConfig, updateFatigueConfig } from "services/fatigueService";
import StaminaReductionConfig from "./components/StaminaReductionConfig";
import OvrReductionConfig from "./components/OvrReductionConfig";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
  },
  card: {
    marginBottom: "24px",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: "16px 24px",
    borderRadius: "12px 12px 0 0",
  },
  cardContent: {
    padding: "24px",
  },
  accordion: {
    boxShadow: "none",
    border: "1px solid #e0e0e0",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: "0",
    },
  },
  accordionSummary: {
    backgroundColor: "#f5f5f5",
    "&.Mui-expanded": {
      backgroundColor: "#e3f2fd",
    },
  },
  saveButton: {
    minWidth: "120px",
    height: "42px",
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: "8px",
    fontSize: "0.875rem",
  },
}));

const FatigueConfig = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    activeOnDiv: null, // Will be set from API response
    enabledForOp: [], // Array of operator IDs where fatigue is enabled
    populatedOperators: {}, // Object with operatorId as key and operator data as value
    staminaReductionConf: [],
    ovrReductionConf: [],
  });
  const [errors, setErrors] = useState({
    activeOnDiv: null,
    staminaReductionConf: [],
    ovrReductionConf: [],
  });
  const [isDirty, setIsDirty] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getFatigueConfig();
      if (response.data) {
        // Use the actual config data from API
        setConfig(response.data);
      } else {
        // Only use fallback if no data is returned
        setConfig({
          activeOnDiv: 9, // Fallback default only if API returns no data
          enabledForOp: [],
          populatedOperators: {},
          staminaReductionConf: [],
          ovrReductionConf: [],
        });
      }
    } catch (error) {
      showSnackbar(
        error.message || "Failed to load fatigue configuration",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const validateConfig = () => {
    const newErrors = {
      activeOnDiv: null,
      staminaReductionConf: [],
      ovrReductionConf: [],
    };

    // Helper function to convert empty strings to numbers
    const toNumber = (value: any) => {
      if (value === "" || value === null || value === undefined) {
        return 0;
      }
      const num = parseInt(value);
      return isNaN(num) ? 0 : num;
    };

    // Validate activeOnDiv
    const activeOnDivValue = toNumber(config.activeOnDiv);
    if (activeOnDivValue < 1 || activeOnDivValue > 10) {
      newErrors.activeOnDiv = "Active on division must be between 1 and 10";
    }

    // Validate stamina reduction configs
    config.staminaReductionConf.forEach((item, index) => {
      const itemErrors = [];

      const minOvr = toNumber(item.minOvr);
      const maxOvr = toNumber(item.maxOvr);
      const staminaLossMin = toNumber(item.staminaLossMin);
      const staminaLossMax = toNumber(item.staminaLossMax);

      if (minOvr > maxOvr) {
        itemErrors.push(
          `Row ${index + 1}: Min OVR cannot be greater than Max OVR`
        );
      }

      if (staminaLossMin > staminaLossMax) {
        itemErrors.push(
          `Row ${index + 1
          }: Min Stamina Loss cannot be greater than Max Stamina Loss`
        );
      }

      if (itemErrors.length > 0) {
        newErrors.staminaReductionConf.push(...itemErrors);
      }
    });

    // Check for overlapping OVR ranges in stamina reduction configs
    const sortedStaminaConfigs = [...config.staminaReductionConf].sort(
      (a, b) => toNumber(a.minOvr) - toNumber(b.minOvr)
    );
    for (let i = 0; i < sortedStaminaConfigs.length - 1; i++) {
      const current = sortedStaminaConfigs[i];
      const next = sortedStaminaConfigs[i + 1];
      const currentMaxOvr = toNumber(current.maxOvr);
      const nextMinOvr = toNumber(next.minOvr);
      if (currentMaxOvr >= nextMinOvr) {
        newErrors.staminaReductionConf.push(
          `OVR ranges overlap: [${toNumber(
            current.minOvr
          )}-${currentMaxOvr}] overlaps with [${nextMinOvr}-${toNumber(
            next.maxOvr
          )}]`
        );
      }
    }

    // Validate OVR reduction configs
    config.ovrReductionConf.forEach((item, index) => {
      const itemErrors = [];

      const minStamina = toNumber(item.minStamina);
      const maxStamina = toNumber(item.maxStamina);
      const teamMinStamina = toNumber(item.teamMinStamina);
      const teamMaxStamina = toNumber(item.teamMaxStamina);

      if (minStamina > maxStamina) {
        itemErrors.push(
          `Row ${index + 1}: Min Stamina cannot be greater than Max Stamina`
        );
      }

      if (teamMinStamina > teamMaxStamina) {
        itemErrors.push(
          `Row ${index + 1
          }: Team Min Stamina cannot be greater than Team Max Stamina`
        );
      }

      // Validate hex color format
      const hexRegex = /^#[0-9A-Fa-f]{6}$/;
      if (!hexRegex.test(item.colorHex)) {
        itemErrors.push(`Row ${index + 1}: Invalid hex color format`);
      }

      if (itemErrors.length > 0) {
        newErrors.ovrReductionConf.push(...itemErrors);
      }
    });

    // Check for overlapping stamina ranges in OVR reduction configs
    const sortedOvrConfigs = [...config.ovrReductionConf].sort(
      (a, b) => toNumber(a.minStamina) - toNumber(b.minStamina)
    );
    for (let i = 0; i < sortedOvrConfigs.length - 1; i++) {
      const current = sortedOvrConfigs[i];
      const next = sortedOvrConfigs[i + 1];
      const currentMaxStamina = toNumber(current.maxStamina);
      const nextMinStamina = toNumber(next.minStamina);
      if (currentMaxStamina >= nextMinStamina) {
        newErrors.ovrReductionConf.push(
          `Stamina ranges overlap: [${toNumber(
            current.minStamina
          )}-${currentMaxStamina}] overlaps with [${nextMinStamina}-${toNumber(
            next.maxStamina
          )}]`
        );
      }
    }

    setErrors(newErrors);
    return !newErrors.activeOnDiv && newErrors.staminaReductionConf.length === 0 && newErrors.ovrReductionConf.length === 0;
  };

  const handleSave = async () => {
    if (!validateConfig()) {
      showSnackbar("Please fix validation errors before saving", "error");
      return;
    }

    // Helper function to convert empty strings to numbers for backend
    const toNumber = (value: any) => {
      if (value === "" || value === null || value === undefined) {
        return 0;
      }
      const num = parseInt(value);
      return isNaN(num) ? 0 : num;
    };

    // Convert empty strings to numbers before sending to backend
    const processedConfig = {
      activeOnDiv: config.activeOnDiv, // Include activeOnDiv from the received config
      enabledForOp: config.enabledForOp || [], // Include enabledForOp array
      staminaReductionConf: config.staminaReductionConf.map(item => ({
        ...item,
        minOvr: toNumber(item.minOvr),
        maxOvr: toNumber(item.maxOvr),
        staminaLossMin: toNumber(item.staminaLossMin),
        staminaLossMax: toNumber(item.staminaLossMax),
      })),
      ovrReductionConf: config.ovrReductionConf.map((item) => ({
        ...item,
        minStamina: toNumber(item.minStamina),
        maxStamina: toNumber(item.maxStamina),
        ovrReduction: toNumber(item.ovrReduction),
        teamMaxStamina: toNumber(item.teamMaxStamina),
        teamMinStamina: toNumber(item.teamMinStamina),
      })),
    };

    setSaving(true);
    try {
      await updateFatigueConfig(processedConfig);
      showSnackbar("Fatigue configuration updated successfully", "success");
      setIsDirty(false);
    } catch (error) {
      showSnackbar(
        error.message || "Failed to update fatigue configuration",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (section, newData) => {
    setConfig((prev) => ({
      ...prev,
      [section]: newData,
    }));
    setIsDirty(true);
  };

  const handleActiveOnDivChange = (event: any) => {
    const value = event.target.value;

    // Allow empty string or only digits
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = value === '' ? null : parseInt(value);

      setConfig(prev => ({
        ...prev,
        activeOnDiv: numValue,
      }));
      setIsDirty(true);

      // Clear error if value is valid
      if (numValue !== null && numValue >= 1 && numValue <= 10) {
        setErrors(prev => ({
          ...prev,
          activeOnDiv: null,
        }));
      } else if (numValue !== null) {
        setErrors(prev => ({
          ...prev,
          activeOnDiv: "Active on division must be between 1 and 10",
        }));
      }
    }
  };

  const handleEnabledOperatorsChange = (selectedOperatorIds: any) => {
    // selectedOperatorIds is an array of operator IDs where fatigue should be enabled
    setConfig(prev => ({
      ...prev,
      enabledForOp: selectedOperatorIds,
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  if (loading) {
    return (
      <ContentShell title="Fatigue Configuration" loading={true}>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </ContentShell>
    );
  }

  return (
    <ContentShell
      title="Fatigue Configuration"
      actions={
        <Button
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          onClick={handleSave}
          disabled={saving || !isDirty}
          className={styles.saveButton}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      }
    >
      <Box className={styles.container}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={styles.card}>
              <CardContent className={styles.cardContent}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  General Settings
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Configure the division level where fatigue becomes active and select operators where the fatigue system is enabled.
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Active on Division"
                      type="text"
                      value={config.activeOnDiv === null ? '' : config.activeOnDiv}
                      onChange={handleActiveOnDivChange}
                      error={Boolean(errors.activeOnDiv)}
                      helperText={errors.activeOnDiv || "Enter a value between 1 and 10"}
                      inputProps={{
                        min: 1,
                        max: 10,
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <OperatorMultiSelect
                      label="Enabled for Operators"
                      value={config.enabledForOp || []}
                      onChange={handleEnabledOperatorsChange}
                      populatedOperators={config.populatedOperators || {}}
                      helperText="Select operators where fatigue system is enabled. Unselected operators will have it disabled."
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Accordion className={styles.accordion} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={styles.accordionSummary}
              >
                <Typography variant="h6" fontWeight="600">
                  Stamina Reduction Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Configure how much stamina players lose based on their OVR
                  ratings after matches.
                </Typography>
                <StaminaReductionConfig
                  data={config.staminaReductionConf}
                  onChange={(newData) =>
                    handleConfigChange("staminaReductionConf", newData)
                  }
                  errors={errors.staminaReductionConf}
                  isDragEnabled={false}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion className={styles.accordion} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={styles.accordionSummary}
              >
                <Typography variant="h6" fontWeight="600">
                  OVR Reduction Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Configure how much OVR reduction players experience based on
                  their stamina levels.
                </Typography>
                <OvrReductionConfig
                  data={config.ovrReductionConf}
                  onChange={(newData) =>
                    handleConfigChange("ovrReductionConf", newData)
                  }
                  errors={errors.ovrReductionConf}
                  isDragEnabled={false}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>

        {/* Display global errors */}
        {(errors.activeOnDiv || errors.staminaReductionConf.length > 0 || errors.ovrReductionConf.length > 0) && (
          <Box mt={3}>
            <Card className={styles.card}>
              <CardContent>
                <Typography variant="h6" color="error" gutterBottom>
                  Validation Errors
                </Typography>
                {errors.activeOnDiv && (
                  <Typography className={styles.errorText}>
                    • General Settings: {errors.activeOnDiv}
                  </Typography>
                )}
                {errors.staminaReductionConf.map((error, index) => (
                  <Typography key={index} className={styles.errorText}>
                    • Stamina Reduction: {error}
                  </Typography>
                ))}
                {errors.ovrReductionConf.map((error, index) => (
                  <Typography key={index} className={styles.errorText}>
                    • OVR Reduction: {error}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </ContentShell>
  );
};

export default FatigueConfig;
